import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import {
	MenuButtonBold,
	MenuButtonBulletedList,
	MenuButtonHighlightColor,
	MenuButtonItalic,
	MenuButtonOrderedList,
	MenuButtonRedo,
	MenuButtonStrikethrough,
	MenuButtonTextColor,
	MenuButtonUnderline,
	MenuButtonUndo,
	MenuControlsContainer,
	MenuDivider,
	MenuSelectHeading,
	MenuSelectTextAlign,
	RichTextEditor,
	useRichTextEditorContext,
	type RichTextEditorRef
} from 'mui-tiptap';
import { FC, memo, ReactNode, useEffect, useRef, useState } from 'react';
import './style.css';

// Gardă pentru controalele din toolbar: Tiptap v3 anulează `commandManager` /
// `extensionManager` la destroy, iar controalele mui-tiptap (MenuSelectHeading,
// MenuSelectTextAlign…) au guard doar pe editorul NULL — cu o instanță DISTRUSĂ crapă
// la `editor.can()` / `extensionManager.extensions`. Două straturi:
// 1. `mounted` — controalele apar abia DUPĂ primul commit: la montarea rutei (randare
//    concurentă de tranziție), distrugerea programată de @tiptap/react
//    (`scheduleDestroy`, setTimeout 1ms) poate rula chiar în pauza de time-slicing,
//    DUPĂ ce garda a trecut dar ÎNAINTE ca toolbar-ul să se randeze — pas în care
//    orice verificare de render e inutilă. După commit, timerul a fost deja rezolvat.
// 2. `isDestroyed` — pentru re-randările ulterioare primite cu o instanță moartă.
const LiveEditorControls: FC<{ children: ReactNode }> = ({ children }) => {
	const editor = useRichTextEditorContext();
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);
	if (!mounted || !editor || editor.isDestroyed) return null;
	return children;
};

interface TextEditorProps {
	onUpdate?: (newVal: string) => void;
	onGetPlain?: (newVal: string) => void;
	descriptionData?: string;
	editable?: boolean;
	/** Accessible name for the contenteditable area (screen readers announce it on focus). */
	ariaLabel?: string;
}

const TextEditorComponent: FC<TextEditorProps> = ({
	onUpdate,
	onGetPlain,
	descriptionData,
	editable,
	ariaLabel
}) => {
	const rteRef = useRef<RichTextEditorRef>(null);

	// `editorProps` se PASEAZĂ doar când avem un aria-label — NU cu valoarea `undefined`.
	// mui-tiptap/Tiptap v3 crapă la crearea editorului dacă primește `editorProps={undefined}`
	// explicit (suprascrie default-ul intern → EditorView fără `dispatchTransaction`):
	// „Cannot read properties of undefined (reading 'dispatchTransaction')". Omiterea
	// prop-ului lasă mui-tiptap să-și folosească default-ul. Numele accesibil ajunge pe
	// zona contenteditable (până acum prop-ul era declarat dar neaplicat).
	const ariaEditorProps = ariaLabel
		? { editorProps: { attributes: { 'aria-label': ariaLabel } } }
		: {};

	return (
		<RichTextEditor
			className="text-editor-style"
			ref={rteRef}
			{...ariaEditorProps}
			// Tiptap v3: StarterKit already ships the Underline extension — adding
			// @tiptap/extension-underline again duplicates the 'underline' name
			// ("[tiptap warn]: Duplicate extension names") and breaks editor creation
			// (TypeError: Cannot read properties of null (reading 'extensions')).
			extensions={[
				StarterKit,
				TextStyle,
				Color,
				TextAlign.configure({
					types: ['heading', 'paragraph']
				}),
				Highlight.configure({ multicolor: true })
			]}
			editable={editable}
			content={descriptionData || '<p>Adaugă descriere</p>'}
			// Controalele sunt gardate prin context (vezi LiveEditorControls) — nu se
			// randează niciodată cu o instanță de editor distrusă.
			renderControls={() => (
				<LiveEditorControls>
					<MenuControlsContainer>
						<MenuButtonUndo />
						<MenuButtonRedo />
						<MenuDivider />

						<MenuSelectHeading />
						<MenuDivider />

						<MenuSelectTextAlign />
						<MenuDivider />

						<MenuButtonTextColor
							defaultTextColor={'#000000'}
							swatchColors={[
								{ value: '#000000', label: 'Black' },
								{ value: '#ffffff', label: 'White' },
								{ value: '#888888', label: 'Grey' },
								{ value: '#ff0000', label: 'Red' },
								{ value: '#ff9900', label: 'Orange' },
								{ value: '#ffff00', label: 'Yellow' },
								{ value: '#00d000', label: 'Green' },
								{ value: '#0000ff', label: 'Blue' }
							]}
						/>
						<MenuDivider />

						<MenuButtonBold />
						<MenuButtonItalic />
						<MenuButtonUnderline />
						<MenuButtonStrikethrough />
						<MenuDivider />

						<MenuButtonHighlightColor
							swatchColors={[
								{ value: '#595959', label: 'Dark grey' },
								{ value: '#dddddd', label: 'Light grey' },
								{ value: '#ffa6a6', label: 'Light red' },
								{ value: '#ffd699', label: 'Light orange' },
								{ value: '#ffff00', label: 'Yellow' },
								{ value: '#99cc99', label: 'Light green' },
								{ value: '#90c6ff', label: 'Light blue' },
								{ value: '#8085e9', label: 'Light purple' }
							]}
						/>
						<MenuDivider />

						<MenuButtonOrderedList />
						<MenuButtonBulletedList />
						<MenuDivider />
					</MenuControlsContainer>
				</LiveEditorControls>
			)}
			onUpdate={() => {
				onUpdate && onUpdate(rteRef.current?.editor?.getHTML() || '<p></p>');
				onGetPlain && onGetPlain(rteRef.current?.editor?.getText() || '');
			}}
		/>
	);
};

TextEditorComponent.defaultProps = {
	onUpdate: undefined,
	onGetPlain: undefined,
	descriptionData: '<p>Adaugă descriere</p>',
	editable: true,
	ariaLabel: undefined
};

// Editorul e cel mai scump nod din formularele aplicației: bara de unelte înseamnă ~20 de
// componente MUI, două selectoare de culoare cu câte opt nuanțe și controale care se
// abonează la starea editorului. Memoizat, tastarea într-un câmp VECIN (numele întrebării,
// o variantă de răspuns) nu îl mai re-randează. Notă: `descriptionData` e conținut INIȚIAL —
// mui-tiptap documentează explicit că schimbările lui nu se reaplică după montare.
export const TextEditor = memo(TextEditorComponent);
